import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '@/components/Footer';
import SiteNavBar from '@/components/SiteNavBar';
import { TrialLink } from '@/components/growth-agent/landing/Bits';
import { PRICE, PRICE_NOTE, priceWords } from '@/components/growth-agent/landing/Pricing';
import JoinedCarousel from '@/components/growth-agent/proof/JoinedCarousel';
import '@/styles/fly-home.css';

/*
 * The Local SEO landing page (/home). It leads with the GBP Growth Agent,
 * whose start flow is the page's one call to action, and keeps every other
 * way to work with Fly below it, each with its own page. Facts come from the
 * product: the agent's features and FAQ (components/growth-agent/landing),
 * its price (Pricing.tsx PRICE), and the products this page already listed.
 * No figures or quotes are invented here.
 */

/* ------------------------------------------------------------ the grid */

type Band = 'top' | 'mid' | 'low';
/** A sample reading, row by row; the centre is the shop. */
const READING: (Band | 'shop')[] = [
  'low', 'mid', 'mid', 'mid', 'low',
  'mid', 'top', 'top', 'mid', 'mid',
  'mid', 'top', 'shop', 'top', 'mid',
  'mid', 'top', 'top', 'top', 'low',
  'low', 'mid', 'mid', 'low', 'low',
];

/** When each point is read: ring by ring out from the shop, sweeping round each ring. */
const READ_AT: number[] = READING.map((_, i) => {
  const r = Math.floor(i / 5) - 2;
  const c = (i % 5) - 2;
  const ring = Math.max(Math.abs(r), Math.abs(c));
  if (ring === 0) return 0;
  const turn = (Math.atan2(r, c) + Math.PI) / (2 * Math.PI);
  return Math.round((ring - 1) * 420 + turn * 360);
});

const BAND_WORDS: Record<Band, string> = { top: 'Top 3', mid: '4 to 10', low: 'Beyond 10' };

function RankGrid() {
  return (
    <figure className="fh-grid-card">
      <figcaption>
        <p className="fh-grid-head">Your rank, read block by block</p>
        <p className="fh-small">
          A sample grid. Each dot is a point around the shop where the agent searches your keyword and notes your place.
        </p>
      </figcaption>
      <div className="fh-map" aria-hidden="true">
        {READING.map((band, i) => (
          <span
            key={i}
            className={band === 'shop' ? 'fh-pt is-shop' : 'fh-pt'}
            data-band={band === 'shop' ? undefined : band}
            style={{ '--fh-d': READ_AT[i] } as CSSProperties}
          >
            <i />
          </span>
        ))}
        <span className="fh-pulse" />
      </div>
      <ul className="fh-legend" aria-label="What the colours mean">
        {(Object.keys(BAND_WORDS) as Band[]).map((b) => (
          <li key={b} data-band={b}>
            {BAND_WORDS[b]}
          </li>
        ))}
      </ul>
    </figure>
  );
}

/* ------------------------------------------------------------- content */

/** What the agent does, from its feature list (variants/features.ts). */
const JOBS = [
  {
    title: 'Reviews answered daily, in your tone',
    line: 'Every new review gets a reply the same day, written to your own reply settings. Anything of 2 stars or lower waits for you to approve, edit or decline.',
  },
  {
    title: 'The profile kept complete and guarded',
    line: 'Missing services, attributes, WhatsApp chat and holiday hours are filled in. Every pass reads the live profile first, so an edit someone else makes on Google is caught and reported.',
  },
  {
    title: 'Posts written and scheduled every week',
    line: 'A week of posts at a time, written for the searches people type and the festival calendar, each with a framed picture from your own photos first.',
  },
  {
    title: 'Local rank read on a grid',
    line: 'Your money keywords are read every week on a grid of points around the shop, spaced for your trade. Every reading is dated, so you can see what moved and when.',
  },
  {
    title: 'Performance watched, the plan adjusted',
    line: 'Calls, direction requests, website clicks and searches from Google, week against week. Stalled search terms and keywords below target become the next posts.',
  },
  {
    title: 'Your website checked, or built',
    line: 'The site is checked every day while it is down and audited for local SEO every month. No website yet? The agent offers to build and publish one.',
  },
  {
    title: 'Told what moved and what is next',
    line: 'A wrap-up on every day something happened and a digest every week, on WhatsApp and email.',
  },
];

const STEPS = [
  { title: 'Connect Google', line: "Sign in on Google's own screen. Fly never sees your password." },
  { title: 'Pick one location', line: 'The trial runs the whole agent on one location for 14 days.' },
  { title: 'Approve on WhatsApp', line: 'Add your WhatsApp number and approve the agent’s work in one tap.' },
];

const PRICE_POINTS = ['14 days free, one location, no card', 'Cancel any time', 'Pay by card at a secure checkout'];

/** The other ways to work with Fly. The ids are the page's old anchors (/home#managed and so on). */
const PATHS = [
  {
    id: 'managed',
    name: 'Managed Local SEO',
    audience: 'For solo and multi-location businesses',
    line: "Fly's Local SEO experts run your Google Business Profile end to end on Fly's platform: reviews, rankings, content, profile protection and reporting. You grant access and the team does the rest.",
    priceINR: 'From ₹2,499/mo plus GST',
    priceUSD: 'From $24.99/mo',
    link: '/managed',
    cta: 'Learn more',
  },
  {
    id: 'fly-lite',
    name: 'Fly Lite',
    audience: 'For solo businesses who prefer WhatsApp',
    line: 'Manage your Google Business Profile yourself, entirely through WhatsApp: review alerts, AI-written replies, profile protection and performance reports where you already are. No apps, no dashboards.',
    link: '/gmb-whatsapp-assistant',
    cta: 'Learn more',
  },
  {
    id: 'fly-web-agencies',
    name: 'Fly Web for Agencies',
    audience: 'For marketing agencies and freelancers',
    line: "Your own white-labelled version of Fly's Local SEO dashboard. Manage every client's Google Business Profile under your agency's brand: your logo, your domain, your clients.",
    link: '/agencies-fly-web',
    cta: 'Learn more',
  },
  {
    id: 'fly-agent-agencies',
    name: 'Fly Agent for Agencies',
    audience: 'For marketing agencies and freelancers, coming soon',
    line: "Connect the AI assistant you already use, Claude, ChatGPT or Gemini, to your clients' Google Business Profiles. Specialised agents handle reviews, rankings, content and reporting in plain conversation.",
    priceINR: 'Coming soon',
    priceUSD: 'Coming soon',
    link: '/fly-agent/agencies',
    cta: 'Get early access',
  },
  {
    id: 'fly-diy',
    name: 'Fly DIY',
    audience: 'For solo and multi-location brands who run it themselves',
    line: 'A full dashboard to manage your Google Business Profile yourself: track rankings, answer reviews, create content, watch competitors and build reports, all in one place.',
    link: '/fly-diy',
    cta: 'Learn more',
  },
];

/** From the agent's FAQ (variants/faq.ts), word for word. */
const FAQ = [
  {
    q: 'Is my Google password safe?',
    a: "You sign in on Google's own screen, so Fly never sees your password. The agent works through the official Google Business Profile API.",
  },
  {
    q: 'What if it writes something wrong?',
    a: 'When the trial starts, Fly does it all. Every change it makes is listed in Activity, and many can be undone there. Reviews of 2 stars or lower always wait for your yes. Prefer to approve everything? Pick Fly asks first and nothing reaches Google until you tap.',
  },
  {
    q: 'How often does it run?',
    a: 'Round the clock: a pass every few hours, day and night, weekends included. Every pass reads the live profile and the reviews first. The weekly jobs, posts, rank readings and the performance review, run on their own days.',
  },
  {
    q: 'Can I stop it?',
    a: 'Yes. Switch to Fly asks first, decline any card, or disconnect Google from the agent home. Cancel any time, from the same place.',
  },
];

const TICK = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12l4.5 4.5L19 7" />
  </svg>
);

declare global {
  interface Window {
    __GEO_COUNTRY__?: string;
  }
}

/* ---------------------------------------------------------------- page */

const FlyHomePage = () => {
  const [isIndianUser, setIsIndianUser] = useState(true);

  useEffect(() => {
    const VERCEL_EDGE_REGIONS = ['SG', 'HK', 'JP', 'KR', 'AU', 'NZ'];

    const detectCountry = async () => {
      const geoCountry = typeof window !== 'undefined' ? window.__GEO_COUNTRY__ : undefined;

      if (geoCountry && VERCEL_EDGE_REGIONS.includes(geoCountry)) {
        try {
          const response = await fetch('/api/geo');
          const data = await response.json();
          if (data.countryCode && data.countryCode !== 'IN') setIsIndianUser(false);
          return;
        } catch { return; }
      }

      if (geoCountry && geoCountry !== 'IN') setIsIndianUser(false);
    };

    detectCountry();
  }, []);

  const price = isIndianUser ? PRICE.INR : PRICE.USD;
  const priceNote = isIndianUser ? PRICE_NOTE.INR : PRICE_NOTE.USD;
  const otherPrice = priceWords(isIndianUser ? 'USD' : 'INR');

  return (
    <>
      <Helmet>
        <title>Fly | Local SEO Platform for Businesses & Agencies</title>
        <meta
          name="description"
          content="Fly runs your Google Business Profile every day: reviews answered, weekly posts, a complete listing and your rank read on a grid. Try the agent free for 14 days."
        />
        <meta property="og:title" content="Fly | Local SEO Platform for Businesses & Agencies" />
        <meta
          property="og:description"
          content="An agent that runs your Google Business Profile every day, plus managed, WhatsApp, agency and DIY ways to work with Fly."
        />
        <meta property="og:url" content="https://www.fly-social.com/home" />
        <link rel="canonical" href="https://www.fly-social.com/home" />
      </Helmet>

      <div className="fh">
        <SiteNavBar />

        <main className="fh-main">
          <section className="fh-hero" aria-labelledby="fh-title">
            <div className="fh-wrap fh-hero-grid">
              <div>
                <h1 id="fh-title" className="fh-h1">
                  Show up when someone nearby searches for what you do.
                </h1>
                <p className="fh-lead">
                  Fly's GBP Growth Agent runs your Google Business Profile every day. It answers reviews in your tone,
                  posts every week, keeps the listing complete and reads your rank on a grid around the shop. You
                  approve on WhatsApp.
                </p>
                <div className="fh-cta">
                  <TrialLink placement="home_hero" className="fh-btn">
                    Start 14 day free trial
                  </TrialLink>
                  <a href="#ways">See other ways to work with Fly</a>
                </div>
                <p className="fh-small">14 days free on one location. No card. Cancel any time.</p>
              </div>
              <RankGrid />
            </div>
          </section>

          {/* who joined Fly lately, the sign-up's list as a fast carousel (Anand, 02 Nov 26) */}
          <JoinedCarousel tone="dark" />

          <section className="fh-sec" aria-labelledby="fh-jobs">
            <div className="fh-wrap">
              <h2 id="fh-jobs" className="fh-h2">
                What the agent does, every day
              </h2>
              <p className="fh-sec-line">
                The work that keeps a listing near the top of the map, done on a schedule rather than when someone
                finds the time.
              </p>
              <ul className="fh-jobs">
                {JOBS.map((job) => (
                  <li key={job.title}>
                    <h3 className="fh-h3">{job.title}</h3>
                    <p>{job.line}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="fh-sec" aria-labelledby="fh-start">
            <div className="fh-wrap fh-start">
              <div>
                <h2 id="fh-start" className="fh-h2">
                  Running today, in three steps
                </h2>
                <ol className="fh-steps">
                  {STEPS.map((step) => (
                    <li key={step.title}>
                      <h3 className="fh-h3">{step.title}</h3>
                      <p>{step.line}</p>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="fh-price">
                <h3 className="fh-h3">One price, one location</h3>
                <p className="fh-amt">
                  <b>{price}</b>
                  <span>
                    per location per month{priceNote ? ` ${priceNote}` : ''}, or {otherPrice}
                  </span>
                </p>
                <ul className="fh-ticks">
                  {PRICE_POINTS.map((point) => (
                    <li key={point}>
                      {TICK}
                      {point}
                    </li>
                  ))}
                </ul>
                <TrialLink placement="home_price" className="fh-btn">
                  Start 14 day free trial
                </TrialLink>
              </div>
            </div>
          </section>

          <section className="fh-sec" id="ways" aria-labelledby="fh-ways">
            <div className="fh-wrap">
              <h2 id="fh-ways" className="fh-h2">
                Other ways to work with Fly
              </h2>
              <p className="fh-sec-line">
                Prefer a team to run it, a WhatsApp assistant, or a dashboard of your own? Every one of these works on
                the same Google Business Profile.
              </p>
              <ul className="fh-paths">
                {PATHS.map((path) => (
                  <li key={path.id} id={path.id} className="fh-path">
                    <div className="fh-path-text">
                      <h3 className="fh-h3">{path.name}</h3>
                      <p className="fh-path-for">{path.audience}</p>
                      <p>{path.line}</p>
                    </div>
                    <div className="fh-path-side">
                      {/* Fly Lite, Fly Web for Agencies and Fly DIY show no price (removed, Anand, 17 Oct 26) */}
                      {path.priceINR && path.priceUSD ? (
                        <p className="fh-path-price">{isIndianUser ? path.priceINR : path.priceUSD}</p>
                      ) : null}
                      <Link to={path.link} className="fh-path-link">
                        {path.cta}
                        <span className="fh-sr"> about {path.name}</span>
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="fh-sec" aria-labelledby="fh-faq">
            <div className="fh-wrap">
              <h2 id="fh-faq" className="fh-h2">
                Questions owners ask first
              </h2>
              <div className="fh-faq">
                {FAQ.map((item) => (
                  <details key={item.q}>
                    <summary>{item.q}</summary>
                    <p>{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="fh-final" aria-labelledby="fh-final">
            <div className="fh-wrap">
              <h2 id="fh-final" className="fh-h2">
                Your listing, minded from today.
              </h2>
              <div className="fh-cta">
                <TrialLink placement="home_closing" className="fh-btn">
                  Start 14 day free trial
                </TrialLink>
              </div>
              <p className="fh-small">14 days free. No card.</p>
            </div>
          </section>
        </main>

        <Footer variant="dark" />
      </div>
    </>
  );
};

export default FlyHomePage;
